import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from "@mui/icons-material";
// @mui
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Container,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
// Import React-Quill and its styles
import ReactQuill from "react-quill";
import { useNavigate, useParams } from "react-router-dom";
import { createBlobInContainer } from "src/utils/azureBlob";
import { get, post, put } from "src/utils/networkUtils";
import "react-quill/dist/quill.snow.css";
import { getStatusList } from "src/utils/getStatus";
import { quillFormats, quillModules } from "../config/quill-text-editor-settings";

interface Category {
	categoryId: string;
	name: string;
}

interface FormErrors {
	title?: string;
	description?: string;
	categoryId?: string;
	imageBanner?: string;
}

export default function AddEditNewsPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const isEdit = Boolean(id);

	const [loading, setLoading] = useState(false);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [categories, setCategories] = useState<Category[]>([]);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string>("");
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		content: "",
		categoryId: "",
		imageBanner: "",
		status: 0, // default status
	});
	const [errors, setErrors] = useState<FormErrors>({});

	const fetchCategories = useCallback(async () => {
		try {
			const response = (await get("/admin/news/categories")) as any;
			setCategories(response || []);
		} catch (error) {
			console.error("Error fetching categories:", error);
		}
	}, []);

	const fetchNewsItem = useCallback(async () => {
		if (!id) return;
		try {
			setLoading(true);
			const response = (await get(`/admin/news/${id}`)) as any;
			const newsItem = response;

			setFormData({
				title: newsItem.title || "",
				description: newsItem.description || "",
				content: newsItem.content || "",
				categoryId: newsItem.categoryId || "",
				imageBanner: newsItem.imageBanner || "",
				status: newsItem.status || "inactive",
			});
		} catch (error) {
			console.error("Error fetching news item:", error);
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		fetchCategories();
		if (isEdit) {
			fetchNewsItem();
		}
	}, [fetchCategories, fetchNewsItem, isEdit]);

	useEffect(() => {
		// Set preview URL when editing and imageBanner exists
		if (formData.imageBanner && !selectedFile) {
			setPreviewUrl(formData.imageBanner);
		}
	}, [formData.imageBanner, selectedFile]);

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			// Check if file is an image
			if (!file.type.startsWith("image/")) {
				alert("Please select an image file");
				return;
			}

			// Check file size (max 5MB)
			if (file.size > 5 * 1024 * 1024) {
				alert("File size must be less than 5MB");
				return;
			}

			setSelectedFile(file);

			// Create preview URL
			const reader = new FileReader();
			reader.onload = (e) => {
				setPreviewUrl(e.target?.result as string);
			};
			reader.readAsDataURL(file);

			// Upload to Azure Blob Storage
			const uploadToAzure = async () => {
				try {
					setUploadingImage(true);
					await createBlobInContainer(file);

					const containerName = import.meta.env.VITE_STORAGE_CONTAINER;
					const storageAccountName = import.meta.env.VITE_STORAGERESOURCENAME;

					const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${file.name}`;

					// Update form data with the Azure URL
					setFormData((prev) => ({
						...prev,
						imageBanner: imageUrl,
					}));
				} catch (error) {
					console.error("Error uploading to Azure:", error);
					alert("Error uploading image. Please try again.");
					// Reset on error
					setSelectedFile(null);
					setPreviewUrl("");
				} finally {
					setUploadingImage(false);
				}
			};

			uploadToAzure();
		}
	};

	const handleRemoveImage = () => {
		setSelectedFile(null);
		setPreviewUrl("");
		setFormData((prev) => ({
			...prev,
			imageBanner: "",
		}));
	};

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: string | number } },
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		// Clear error when user starts typing
		if (errors[name as keyof FormErrors]) {
			setErrors((prev) => ({
				...prev,
				[name]: "",
			}));
		}
	};

	const handleChangeText = (content: string, field: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: content,
		}));
		// Clear error when user starts typing
		if (errors[field as keyof FormErrors]) {
			setErrors((prev) => ({
				...prev,
				[field]: "",
			}));
		}
	};

	const validateForm = () => {
		const newErrors: FormErrors = {};

		if (!formData.title.trim()) {
			newErrors.title = "Title is required";
		}

		if (!formData.description.trim()) {
			newErrors.description = "Description is required";
		}

		if (!formData.categoryId) {
			newErrors.categoryId = "Category is required";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		try {
			setLoading(true);

			if (isEdit) {
				await put(`/admin/news/${id}`, formData);
			} else {
				await post("/admin/news", formData);
			}

			navigate("/dashboard/news");
		} catch (error) {
			console.error("Error saving news:", error);
			alert("Error saving news. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		navigate("/dashboard/news");
	};

	if (isEdit && loading) {
		return (
			<Container>
				<Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
					<CircularProgress />
				</Box>
			</Container>
		);
	}

	return (
		<>
			<Helmet>
				<title>{isEdit ? "Edit News" : "Create News"}</title>
			</Helmet>

			<Container>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="h4" gutterBottom>
						{isEdit ? "Edit News" : "Create News"}
					</Typography>
				</Stack>

				<Card sx={{ p: 3 }}>
					<Stack spacing={3}>
						<TextField
							name="title"
							label="Title"
							value={formData.title}
							onChange={handleChange}
							fullWidth
							error={!!errors.title}
							helperText={errors.title}
							variant="outlined"
						/>

						{/* Image Banner Upload */}
						<Box>
							<Typography variant="subtitle1" gutterBottom>
								Banner Image
							</Typography>

							{previewUrl ? (
								<Box sx={{ position: "relative", display: "inline-block" }}>
									<Box
										component="img"
										src={previewUrl}
										alt="Banner preview"
										sx={{
											width: "100%",
											maxWidth: 400,
											height: 200,
											objectFit: "cover",
											borderRadius: 1,
											border: "1px solid #ddd",
											opacity: uploadingImage ? 0.5 : 1,
										}}
									/>
									{uploadingImage && (
										<Box
											sx={{
												position: "absolute",
												top: "50%",
												left: "50%",
												transform: "translate(-50%, -50%)",
											}}
										>
											<CircularProgress size={40} />
										</Box>
									)}
									<IconButton
										onClick={handleRemoveImage}
										disabled={uploadingImage}
										sx={{
											position: "absolute",
											top: 8,
											right: 8,
											backgroundColor: "rgba(255, 255, 255, 0.8)",
											"&:hover": {
												backgroundColor: "rgba(255, 255, 255, 0.9)",
											},
										}}
										size="small"
									>
										<DeleteIcon />
									</IconButton>
								</Box>
							) : (
								<Box
									sx={{
										border: "2px dashed #ddd",
										borderRadius: 1,
										p: 3,
										textAlign: "center",
										cursor: "pointer",
										"&:hover": {
											borderColor: "primary.main",
											backgroundColor: "action.hover",
										},
									}}
									onClick={() => document.getElementById("image-upload")?.click()}
								>
									<CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
									<Typography variant="body1" color="text.secondary">
										Click to upload banner image
									</Typography>
									<Typography variant="body2" color="text.disabled">
										Supports: JPG, PNG, GIF (Max 5MB)
									</Typography>
								</Box>
							)}

							<input id="image-upload" type="file" accept="image/*" onChange={handleFileSelect} style={{ display: "none" }} />

							{!previewUrl && (
								<Button
									variant="outlined"
									startIcon={uploadingImage ? <CircularProgress size={20} /> : <CloudUploadIcon />}
									onClick={() => document.getElementById("image-upload")?.click()}
									disabled={uploadingImage}
									sx={{ mt: 1 }}
								>
									{uploadingImage ? "Uploading..." : "Choose Image"}
								</Button>
							)}
						</Box>

						<Box>
							<Typography variant="subtitle1" gutterBottom>
								Description
							</Typography>
							<ReactQuill
								value={formData.description}
								onChange={(content) => handleChangeText(content, "description")}
								modules={quillModules}
								formats={quillFormats}
							/>
						</Box>

						<Box>
							<Typography variant="subtitle1" gutterBottom>
								Content
							</Typography>
							<ReactQuill
								value={formData.content}
								onChange={(content) => handleChangeText(content, "content")}
								modules={quillModules}
								formats={quillFormats}
							/>
						</Box>

						<FormControl fullWidth error={!!errors.categoryId}>
							<InputLabel>Category</InputLabel>
							<Select name="categoryId" value={formData.categoryId} onChange={handleChange} label="Category">
								{categories.map((category) => (
									<MenuItem key={category.categoryId} value={category.categoryId}>
										{category.name}
									</MenuItem>
								))}
							</Select>
							{errors.categoryId && (
								<Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
									{errors.categoryId}
								</Typography>
							)}
						</FormControl>

						<FormControl>
							<InputLabel>Status</InputLabel>
							<Select name="status" value={formData?.status || 0} onChange={handleChange} label="Status">
								{Object.values(getStatusList()).map((item) => (
									<MenuItem key={item.value} value={item.value}>
										{item.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<Stack direction="row" spacing={2} justifyContent="flex-end">
							<Button onClick={handleCancel} variant="outlined" disabled={loading}>
								Cancel
							</Button>
							<Button onClick={handleSubmit} variant="contained" disabled={loading}>
								{loading ? <CircularProgress size={24} /> : isEdit ? "Update" : "Create"}
							</Button>
						</Stack>
					</Stack>
				</Card>
			</Container>
		</>
	);
}
